import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SuggestHotel } from '../../models';
import { ApiService } from '../../services/api.service';
import {SearchService} from "../../services/search/search.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";

@Component({
  selector: 'app-hotel-name-search',
  templateUrl: './hotel-name-search.component.html',
  styleUrls: ['./hotel-name-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class HotelNameSearchComponent implements OnInit {
  messages: any = {};

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    private cdRef: ChangeDetectorRef,
    private searchService: SearchService,
    private apiService: ApiService
  ) {
    this.messages = {
      findHotelByName: this.hotelToursPageConfig.translates.filters.findHotelByName,
      searchHotelText: this.hotelToursPageConfig.translates.filters.searchHotelText,
      searchHotelNameText: this.hotelToursPageConfig.translates.filters.searchHotelNameText,
    }
  }

  get searchNameLength() {
    return this.searchNameRef.nativeElement.value.length;
  }

  @ViewChild('searchName', { static: true, read: ElementRef }) searchNameRef!: ElementRef;

  isHotelSelected = false;
  inFocus = false;
  isLoading = false;
  hotels: SuggestHotel[] = [];
  isShowClearBtn = false;

  private static prepareDesktopMainForm(hotel: SuggestHotel) {
    // в desktop нужно все делать через обращение к форме :(
    const theMainForm = (window as any).mainForm;
    theMainForm.hotel_data = {};
    theMainForm.hotel_data[hotel.id] = {
      id: hotel.id,
      name: hotel.name,
      regionName: hotel.region.name,
    };

    theMainForm.region_id = hotel.region.id;
    theMainForm.country_id = hotel.country.id;
    theMainForm.hotel_id = hotel.id;
    theMainForm.hotels = [hotel.id];

    theMainForm.changeHiddenValue('sfHiddenCountry', hotel.country.id);
    theMainForm.changeHiddenValue('sfHiddenRegion', hotel.region.id);
    theMainForm.changeHiddenValue('sfHiddenHotel', hotel.id);

    theMainForm.renderDestinationLabel();

    theMainForm.resetDatepickerDate();
    theMainForm.resetNights();
    theMainForm.updateDesc();

    theMainForm.onSubmit();
  }

  ngOnInit(): void {
    fromEvent(this.searchNameRef.nativeElement, 'keyup')
      .pipe(
        map((event: any) => event.target.value),
        tap((val) => {
          this.isShowClearBtn = val.length > 0;
        }),
        filter((val: string) => val.length > 2),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((value) => {
          let countryId = this.searchService.getLastSearchParams().countryId;
          if (!countryId) {
            countryId = this.hotelToursPageConfig.defaultSuggestHotelCountryId;
          }
          return this.apiService.suggestHotels(value, countryId).pipe(finalize(() => (this.isLoading = false)));
        })
      )
      .subscribe((hotels: SuggestHotel[]) => {
        this.hotels = hotels;
        this.cdRef.detectChanges();
      });
  }

  onFocus() {
    this.inFocus = true;
    this.searchNameRef.nativeElement.focus();
  }

  onFocusOut() {
    this.inFocus = this.searchNameRef.nativeElement.value.length !== 0;
  }

  onClick(hotel: SuggestHotel) {
    if (environment.mobile) {
      const event = new CustomEvent('search:select-hotel', {
        detail: {
          hotel: {
            id: hotel.id,
            countryId: hotel.country.id,
            name: hotel.name,
          },
        },
      });
      setTimeout(() => {
        window.dispatchEvent(event);
      }, 250);
    } else {
      this.searchNameRef.nativeElement.value = hotel.name;
      this.isHotelSelected = true;
      this.hotels = [];

      HotelNameSearchComponent.prepareDesktopMainForm(hotel);
    }
  }

  onClickClear() {
    this.searchNameRef.nativeElement.value = '';
    this.isHotelSelected = false;
    this.inFocus = false;
    this.hotels = [];
    this.isShowClearBtn = false;

    // Если не мобильная версия и есть данные о предпоследнем поиске
    // если взять последний, то там будет поиск по самому отелю, который стираем
    // if (!environment.mobile && this.searchService?.prevSearchParams && this.searchService?.prevSearchOptions) {
    //   // TODO: Сбросили, повторили поиск. А в форме выбран конкретный отель.
    //   //  Итог: выдача не соответствует форме поиска
    //   const formParams = this.searchService?.prevSearchParams;
    //   const formOptions = this.searchService?.prevSearchOptions;
    //   // Убираем данные об отели
    //   const searchEvent = new CustomEvent('search:start-new', {
    //     detail: {
    //       formParams,
    //       ...formOptions,
    //     },
    //   });
    //   window.dispatchEvent(searchEvent);
    // }
  }

  mainPhotoSrc(photo) {
    if (photo) {
      return (window as any).app.staticDomain + '/img/h/sm/' + photo;
    }

    return this.hotelToursPageConfig.noPhotoLink;
  }
}
